import * as types from "../actions/actionTypes";
import { loadSessionCache } from "../offline/sessionCache";

const STORAGE_KEY = "sessionCode";

const readStoredCode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const storeCode = (code) => {
  try {
    if (code) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // storage unavailable (private mode) - the session just won't be remembered
  }
};

// What the device remembers about a session: its current selection, this device's role and
// a host's change that still has to reach the server, so the lyrics show before (or
// without) the server answering and an offline choice survives a reload.
const cachedSelection = (code) => {
  const cached = loadSessionCache(code);
  return cached
    ? {
        SongId: cached.session.SongId ?? null,
        VerseNum: cached.session.VerseNum ?? -1,
        isHost: cached.session.isHost === true,
        fromCache: true,
        pendingSelection: cached.session.pendingSelection || null,
      }
    : { SongId: null, VerseNum: -1, isHost: false, fromCache: false, pendingSelection: null };
};

// What the server tells about the room, reset with every session.
const roomState = () => ({
  Reactions: 0, // hearts so far
  Listeners: 0, // people connected right now
  SetId: null, // the set the session was started from
  SetName: null,
  Created: null,
  previousVerse: null, // the section shown before the current one (same song), for the fading tail
  peekVerseNum: null, // a guest looking at another section; null shows the live one
  sectionSince: null, // when the current section went on screens (ms), for "held longest"
  sectionTimes: {}, // "songId:verse" -> seconds on screens
});

// A function so the remembered code (and its cache) is read when the store is created, not when the module loads.
export const createInitialSessionState = () => {
  const Code = readStoredCode();
  return {
    Code,
    ...cachedSelection(Code), // SongId, VerseNum, isHost, fromCache, pendingSelection
    ...roomState(),
    FollowVerse: true,
    connectionState: "disconnected", // see ConnectionState in realtime/socketClient
    isConnected: false,
    hasConnected: false, // at least once since the page loaded
    isOnline: typeof navigator === "undefined" || navigator.onLine !== false,
    joinedSession: false,
    isLoaded: false, // the session has been fetched at least once since its code was set
    notFound: false,
    ended: false,
    error: null,
    // pendingSelection (host changes made offline, sent when the connection is back) comes
    // from the cache above, so a host who reloads while offline does not lose it
  };
};

// Adds the time the outgoing section spent on screens; starts the clock for the new one.
const accountTime = (state, now) => {
  if (state.sectionSince == null || !state.SongId || state.VerseNum < 0) return { sectionSince: now, sectionTimes: state.sectionTimes };
  const key = `${state.SongId}:${state.VerseNum}`;
  const seconds = Math.max(0, (now - state.sectionSince) / 1000);
  return { sectionSince: now, sectionTimes: { ...state.sectionTimes, [key]: (state.sectionTimes[key] || 0) + seconds } };
};

export default function sessionReducer(state = createInitialSessionState(), action) {
  switch (action.type) {
    case types.SESSION_SET_CODE:
      storeCode(action.code);
      return {
        ...state,
        Code: action.code,
        ...cachedSelection(action.code), // includes a pending host change kept for this code
        ...roomState(),
        joinedSession: false,
        isLoaded: false,
        notFound: false,
        ended: false,
        error: null,
      };

    case types.SESSION_CLEAR:
      storeCode(null);
      return {
        ...state,
        Code: null,
        SongId: null,
        VerseNum: -1,
        ...roomState(),
        joinedSession: false,
        isHost: false,
        notFound: false,
        ended: false,
        pendingSelection: null,
      };

    case types.SESSION_ERROR:
      return { ...state, error: action.message };

    case types.SESSION_LOAD_SUCCESS: {
      if (action.session.Code && action.session.Code !== state.Code) return state;
      const keepLocal = state.pendingSelection && action.session.IsHost === true;
      return {
        ...state,
        SongId: keepLocal ? state.SongId : action.session.SongId || null,
        VerseNum: keepLocal ? state.VerseNum : action.session.VerseNum ?? -1,
        isHost: action.session.IsHost === true,
        pendingSelection: action.session.IsHost === true ? state.pendingSelection : null,
        Reactions: Math.max(state.Reactions, action.session.Reactions ?? 0),
        Listeners: action.session.Listeners ?? state.Listeners,
        SetId: action.session.SetId ?? null,
        SetName: action.session.SetName ?? null,
        Created: action.session.Created ?? state.Created,
        sectionSince: state.sectionSince ?? (action.time || Date.now()),
        isLoaded: true,
        notFound: false,
        fromCache: false,
      };
    }

    case types.SESSION_ENDED:
      return action.code === state.Code ? { ...state, ended: true, pendingSelection: null, error: "The session has ended" } : state;

    case types.SESSION_LOAD_FAIL:
      if (action.code !== state.Code) return state;
      return action.notFound
        ? { ...state, isLoaded: true, notFound: true, error: `Session ${action.code} is no longer available` }
        : { ...state, isLoaded: true };

    case types.SESSION_SET_SONG: {
      if (state.isHost && state.pendingSelection && !action.local) return state;
      const SongId = action.SongId || null;
      const VerseNum = action.VerseNum ?? -1;
      if (SongId === state.SongId && VerseNum === state.VerseNum) return state;
      return {
        ...state,
        SongId,
        VerseNum,
        previousVerse: SongId === state.SongId && state.VerseNum >= 0 && state.VerseNum !== VerseNum ? state.VerseNum : null,
        peekVerseNum: SongId === state.SongId ? state.peekVerseNum : null,
        ...accountTime(state, action.time || Date.now()),
      };
    }

    case types.SESSION_SET_VERSE: {
      if (state.isHost && state.pendingSelection && !action.local) return state;
      if (action.VerseNum === state.VerseNum) return state;
      return {
        ...state,
        VerseNum: action.VerseNum,
        previousVerse: state.VerseNum >= 0 ? state.VerseNum : null,
        ...accountTime(state, action.time || Date.now()),
      };
    }

    case types.SESSION_PEEK:
      return { ...state, peekVerseNum: action.verseNum ?? null };

    case types.SESSION_REACTIONS:
      return { ...state, Reactions: Math.max(0, action.reactions ?? 0) };

    case types.SESSION_LISTENERS:
      return { ...state, Listeners: Math.max(0, action.count ?? 0) };

    case types.SESSION_SET_FOLLOW:
      return { ...state, FollowVerse: action.followState };

    case types.SOCKET_STATE_CHANGED: {
      const isConnected = action.state === "connected";
      return {
        ...state,
        connectionState: action.state,
        isConnected,
        hasConnected: state.hasConnected || isConnected,
        // group membership is lost with the connection and restored by a fresh join
        joinedSession: isConnected && state.joinedSession,
      };
    }

    case types.ONLINE_CHANGED:
      return { ...state, isOnline: action.isOnline };

    case types.SESSION_JOINED:
      return action.code === state.Code ? { ...state, joinedSession: action.result === true } : state;

    case types.SESSION_JOIN_FAILED:
      return { ...state, joinedSession: false };

    case types.SESSION_PENDING_SELECTION:
      return { ...state, pendingSelection: { SongId: action.SongId, VerseNum: action.VerseNum } };

    case types.SESSION_PENDING_SENT:
      return { ...state, pendingSelection: null };

    default:
      return state;
  }
}
